import * as d3 from "d3";
import { useEffect, useRef } from "react";

import { useNavigate } from "@/hooks/useNavigate";

type Segment = { id: string; startHour: number; endHour: number; startAt: string; endAt: string; activityName: string };

type WeeklyBarChartDataProps = {
  x: d3.ScaleBand<number>;
  y: d3.ScaleLinear<number, number>;
  barWidth: number;
  perDayFromSegments: Array<Map<string, number>>;
  keys: string[];
  entrySegmentsMap: Map<number, Segment[]>;
};

export function WeeklyBarChartData({
  x,
  y,
  barWidth,
  perDayFromSegments,
  keys,
  entrySegmentsMap,
}: WeeklyBarChartDataProps) {
  const navigate = useNavigate();

  const layerRef = useRef<SVGGElement>(null);

  useEffect(() => {
    if (!layerRef.current) return;
    const selection = d3.select(layerRef.current);

    // クリアして再描画（単純実装）
    selection.selectAll("*").remove();

    // 曜日ごとの積み上げバー
    perDayFromSegments.forEach((m, dayIndex) => {
      let acc = 0;
      const entries = keys.map((k) => ({ key: k, value: m.get(k) ?? 0 })).filter((d) => d.value > 0);
      entries.forEach((e) => {
        const h0 = acc;
        const h1 = acc + e.value;
        acc = h1;
        const rect = selection
          .append("rect")
          .attr("class", e.key)
          .attr("x", x(dayIndex) ?? 0)
          .attr("width", barWidth)
          .attr("y", y(h0))
          .attr("height", 0)
          .attr("rx", 2)
          .attr("ry", 2);

        rect
          .transition()
          .duration(600)
          .ease(d3.easeCubicOut)
          .attr("y", y(h1))
          .attr("height", y(h0) - y(h1));
      });
    });

    // クリック用透明オーバーレイ
    for (
      let dayIndex = 0 as 0 | 1 | 2 | 3 | 4 | 5 | 6;
      dayIndex < 7;
      dayIndex = (dayIndex + 1) as 0 | 1 | 2 | 3 | 4 | 5 | 6
    ) {
      const entrySegments = entrySegmentsMap.get(dayIndex) ?? [];
      entrySegments.forEach((entrySegment) => {
        const y0 = y(entrySegment.endHour);
        const h = y(entrySegment.startHour) - y(entrySegment.endHour);
        if (h <= 0) return;
        selection
          .append("rect")
          .attr("class", "segment-overlay")
          .attr("data-entry-id", entrySegment.id)
          .attr("data-entry-start-at", entrySegment.startAt)
          .attr("data-entry-end-at", entrySegment.endAt)
          .attr("data-entry-name", entrySegment.activityName)
          .attr("x", x(dayIndex) ?? 0)
          .attr("width", barWidth)
          .attr("y", y0)
          .attr("height", h)
          .attr("fill", "transparent")
          .style("cursor", "pointer")
          .on("click", (_event) => navigate(`/modals/entries/${entrySegment.id}`));
      });
    }
  }, [x, y, barWidth, perDayFromSegments, keys, entrySegmentsMap, navigate]);

  return (
    <g
      ref={layerRef}
      className="data-layer"
    />
  );
}
